import React, { useEffect, useState } from "react";
import MuiDialog from "@material-ui/core/Dialog";
import { withStyles } from "@material-ui/styles";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  computeFinishDate,
  formatDateTime,
  weeksBetween,
} from "./coachingDates";

const Dialog = withStyles({
  paper: {
    maxWidth: 520,
    width: "100%",
    borderRadius: 14,
  },
})(MuiDialog);

export const CoachingFormMode = {
  Assign: "assign", // manual coach assignment (pick coach)
  Update: "update", // extend / re-date an open coaching (coach fixed)
};

// Shared form for "Manuel Koç Ata" and "Süre Güncelle". Collects StartDate +
// WeekCount (+ CoachId when assigning) and previews the backend-computed
// FinishDate. The page owns the API call; `submitting` locks the form.
export default function CoachingFormDialog({
  open,
  mode,
  coaching, // record being updated (Update mode)
  coachOptions = [], // [{ id, label }]
  coachesLoading,
  submitting,
  onSubmit,
  onClose,
}) {
  const isUpdate = mode === CoachingFormMode.Update;

  const [coachId, setCoachId] = useState("");
  const [startDate, setStartDate] = useState(new Date());
  const [weekCount, setWeekCount] = useState("1");

  // Reset every time the dialog opens. Update prefills the record's current
  // start and duration so extending only means changing the week count.
  useEffect(() => {
    if (!open) return;
    setCoachId("");
    if (isUpdate && coaching) {
      setStartDate(coaching.StartDate ? new Date(coaching.StartDate) : new Date());
      setWeekCount(String(weeksBetween(coaching.StartDate, coaching.EndDate)));
    } else {
      setStartDate(new Date());
      setWeekCount("1");
    }
  }, [open, isUpdate, coaching]);

  const weeks = /^\d+$/.test(weekCount) ? Number(weekCount) : 0;
  const finishDate = computeFinishDate(startDate, weeks);
  const isValid = !!startDate && weeks >= 1 && (isUpdate || !!coachId);

  const handleSubmit = () => {
    if (!isValid || submitting) return;
    onSubmit({
      CoachId: isUpdate ? undefined : Number(coachId),
      StartDate: startDate,
      WeekCount: weeks,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      // The date picker renders in a portal outside the dialog; without this
      // MUI's focus trap steals focus back and the time list can't be used.
      disableEnforceFocus
    >
      <div className="uc-dialog">
        <h3 className="uc-dialog-title">
          {isUpdate ? "Koçluk Süresini Güncelle" : "Manuel Koç Ata"}
        </h3>
        <p className="uc-dialog-subtitle">
          {isUpdate
            ? "Mevcut kayıt kapatılır ve aynı koçla yeni tarihlerle yeni bir kayıt oluşturulur."
            : "Kullanıcının aktif koçluk kredisi varsa o kullanılır, yoksa kredisiz kayıt oluşturulur."}
        </p>

        <div className="uc-field">
          <label className="uc-label">Koç</label>
          {isUpdate ? (
            <div className="uc-readonly">
              {(coaching && coaching.CoachName) || "-"}
              {coaching && coaching.CoachEmail && (
                <span className="uc-muted"> · {coaching.CoachEmail}</span>
              )}
            </div>
          ) : (
            <select
              className="uc-input"
              value={coachId}
              onChange={(e) => setCoachId(e.target.value)}
              disabled={coachesLoading || submitting}
            >
              <option value="">
                {coachesLoading
                  ? "Koçlar yükleniyor…"
                  : coachOptions.length === 0
                  ? "Uygun koç bulunamadı"
                  : "Koç seçiniz"}
              </option>
              {coachOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="uc-field-row">
          <div className="uc-field">
            <label className="uc-label">Başlangıç</label>
            <DatePicker
              selected={startDate}
              onChange={(d) => setStartDate(d)}
              showTimeSelect
              timeIntervals={15}
              timeCaption="Saat"
              dateFormat="dd.MM.yyyy HH:mm"
              timeFormat="HH:mm"
              className="uc-input"
              portalId="uc-datepicker-portal"
              popperClassName="uc-datepicker-popper"
              disabled={submitting}
            />
          </div>

          <div className="uc-field uc-field-narrow">
            <label className="uc-label">Süre (hafta)</label>
            <input
              className="uc-input"
              type="number"
              min={1}
              step={1}
              value={weekCount}
              onChange={(e) => setWeekCount(e.target.value)}
              disabled={submitting}
            />
          </div>
        </div>

        <div className="uc-preview">
          <span className="uc-muted">Bitiş tarihi:</span>{" "}
          <strong>{finishDate ? formatDateTime(finishDate) : "-"}</strong>
          {weekCount !== "" && weeks < 1 && (
            <span className="uc-error"> Süre en az 1 hafta olmalıdır.</span>
          )}
        </div>

        <div className="uc-dialog-actions">
          <button
            type="button"
            className="uc-btn uc-btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Vazgeç
          </button>
          <button
            type="button"
            className="uc-btn uc-btn-primary"
            onClick={handleSubmit}
            disabled={!isValid || submitting}
          >
            {submitting ? "Kaydediliyor…" : isUpdate ? "Güncelle" : "Koç Ata"}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
